import { useCallback, useEffect, useRef, useState } from "react";
import Loader from "../../components/Loader/Loader";
import Page from "./Page";
import "./GlobalSearch.scss";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  clickStreamUser,
  convertTimeFormat,
  getProcureInfo,
  redirectToPageUtil,
  SessionKeys,
} from "../../utils/AppUtils";
import {
  getGlobalSearchCourse,
  getGlobalSearchCourseClear,
  getGlobalSearchLP,
  getGlobalSearchLPClear,
} from "../../redux/learningPath/actions";
import _, { debounce } from "lodash";
import NotFoundComponent from "../../components/NewDesign/NotFound/NotFoundComponent";
import { useTranslation } from "react-i18next";
import ProductCardFinwell from "../components/card/ProductCardFinwell";
import { useNavigate, useSearchParams } from "react-router-dom";
import SectionHeader from "../components/SectionHeader/SectionHeader";
import ActionPathExploreComp from "../explore/ActionPathExploreComp";
const GlobalSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const globalSearchLPResponse = useSelector(
    (state) => state.learningPath.globalSearchLP,
    shallowEqual,
  );
  const globalSearchCourseResponse = useSelector(
    (state) => state.learningPath.globalSearchCourse,
    shallowEqual,
  );
  const authBroker = useSelector((state) => state.auth.authBroker);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [courseResult, setCourseResult] = useState([]);
  const [lpResult, setLpResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);
  const [pillarData, setPillarData] = useState(false);
  const [courseData, setCourseData] = useState({});
  const [expandedPillarId, setExpandedPillarId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState(null);
  const [total, setTotal] = useState(0);
  const [apiCalled, setApiCalled] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    const search = searchParams.get("search");
    if (search) {
      getLPData(search);
      setQuery(search);
    }
    inputRef.current?.focus();
    return () => {
      dispatch(getGlobalSearchLPClear());
      dispatch(getGlobalSearchCourseClear());
    };
  }, []);

  useEffect(() => {
    const search = searchParams.get("search");
    if (search && search !== query) {
      getLPData(search);
      setQuery(search);
    }
  }, [searchParams]);

  useEffect(() => {
    getLPData(query);
  }, [sort]);

  useEffect(() => {
    if (apiCalled) {
      const lpData = globalSearchLPResponse?.payload?.payload?.records || [];
      const courseData =
        globalSearchCourseResponse?.payload?.payload?.courseList || [];
      const totalAssets =
        (globalSearchLPResponse?.payload?.payload?.total_records ?? 0) * 1 +
        (globalSearchCourseResponse?.payload?.payload?.total_record ?? 0) * 1;
      setTotal(totalAssets);
      setLpResult(lpData);
      setCourseResult(courseData);
      handlePillarData(courseData);
      setIsLoading(false);
      if (query) {
        setSearchParams({ search: query });
      }
      setShowNotFound(
        lpData?.length === 0 && courseData?.length === 0 && query,
      );
    }
  }, [globalSearchLPResponse, globalSearchCourseResponse]);

  const getLPData = (searchText) => {
    if (!searchText) {
      return;
    }
    setApiCalled(true);
    setIsLoading(true);
    dispatch(
      getGlobalSearchLP({
        module: "gsLp",
        postData: {
          token: localStorage.getItem(SessionKeys.API_TOKEN_BROKER)
            ? localStorage.getItem(SessionKeys.API_TOKEN_BROKER)
            : "",
          search: searchText?.trim(),
          sort: sort === null ? "6" : sort,
        },
      }),
    );
    dispatch(
      getGlobalSearchCourse({
        module: "gsCourse",
        postData: {
          token: localStorage.getItem(SessionKeys.API_TOKEN_BROKER)
            ? localStorage.getItem(SessionKeys.API_TOKEN_BROKER)
            : "",
          search: searchText?.trim(),
          sort: sort === null ? "6" : sort,
        },
      }),
    );
  };
  const debouncedFetch = useCallback(debounce(getLPData, 1000), []);

  const handleChange = (e) => {
    const value = e.target.value;
    setShowNotFound(false);
    setQuery(value);
    debouncedFetch(value);
  };
  const handleSort = (e) => {
    clickStreamUser(
      "sort_selected_in_global_search",
      {
        userId: authBroker?.payload?.user?.user_id,
        selectedValue: e.target.value,
      },
      authBroker,
    );
    const value = e.target.value;
    setSort(() => value);
  };
  const handlePillarData = (courseList) => {
    const pillarMap = new Map();

    courseList.forEach((course) => {
      const pillarKey = course.pillar_id || "unknown_id";
      const pillarName = course.pillar_name || "Unknown";
      const pillarId = `${pillarKey}|${pillarName}`;

      // Tag and card building logic
      let tags = [];
      let btnTitle = t("Start course");

      if (course.provider?.provider_display_name) {
        tags.push(course.provider.provider_display_name);
      }
      tags.push("Course");

      if (course.lessons) {
        tags.push(`${course.lessons} Module${course.lessons > 1 ? "s" : ""}`);
      }
      if (course.duration) {
        tags.push(`${course.duration} ${course.durationUnit}`);
      }
      if (course.views) {
        tags.push(`${course.views} ${t("View")}${course.views > 1 ? "s" : ""}`);
      }

      if (course.crs_progress > 0 && course.crs_progress < 100) {
        btnTitle = t("Resume course");
      } else if (course.crs_progress >= 100) {
        btnTitle = t("Revisit course");
      }

      const priceInfo = getProcureInfo(course.purchased_info);
      course.is_home = true;

      const cardData = {
        id: course?.courseId,
        is_like: course?.is_like ?? false,
        is_bookmark: course?.is_bookmark ?? false,
        dbId: course?.list_id,
        productType: course?.product_type,
        productOrigin: course?.source,
        title: course?.name,
        logoURL: course?.provider?.provider_img || "",
        bannerURL: course?.imageURL,
        tagsArray: tags,
        description: course?.short_description,
        btnTitle: btnTitle,
        progress: course?.crs_progress,
        showLikeMenu: true,
        btnAction: () =>
          redirectToPageUtil(
            course,
            navigate,
            authBroker,
            setCourseData,
            dispatch,
          ),
        descAction: () =>
          redirectToPageUtil(
            course,
            navigate,
            authBroker,
            setCourseData,
            dispatch,
          ),
        bannerAction: () =>
          redirectToPageUtil(
            course,
            navigate,
            authBroker,
            setCourseData,
            dispatch,
          ),
        shareClicked: () =>
          redirectToPageUtil(
            course,
            navigate,
            authBroker,
            setCourseData,
            dispatch,
            "share",
          ),
        footerLineComp: "",
        showHeader: true,
        showBody: !!course?.short_description,
        showFooter: true,
        showProgress: false,
        cardClassName: "",
        rightBtnIcon: "/images/arrow-right.svg",
        lefttBtnIcon: "",
        priceInfo,
        status: course?.crs_status,
        asset_type: course?.asset_name,
        duration: convertTimeFormat(course.duration, true, t),
        monitoring_name: course?.monitoring_name,
        asset_icon: course?.asset_icon,
        monitoring_icon: course?.monitoring_icon,
        fromPage: "explore",
        // isCoachPick: course?.is_coachpick,
      };

      if (!pillarMap.has(pillarId)) {
        pillarMap.set(pillarId, []);
      }
      pillarMap.get(pillarId).push(cardData);
    });

    setPillarData(pillarMap);
  };
  const handleViewAll = (pillarId) => {
    clickStreamUser(
      "view_more_clicked",
      {
        userId: authBroker?.payload?.user?.user_id,
      },
      authBroker,
    );
    setExpandedPillarId(pillarId);
  };
  return (
    <Page>
      <Loader start={isLoading} />
      <section className="money-section search-section">
        <div className="assssbanner">
          <img
            src="/images/finwell-img/Header.png"
            alt="banner"
            width="1440"
            height="244"
            className="img-fluid"
          />
        </div>
        <div className="search-section-inner">
          <h1>{t("find_what_matters_to_you")}</h1>
          <p className="parsec">{t("search_for_content_and_resources")}</p>
          {false && (
            <div className="search-container">
              <span className="search-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                  />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search content by keywords"
                className="search-input"
                ref={inputRef}
                value={query}
                onChange={handleChange}
              />
            </div>
          )}
          {(lpResult.length > 0 || courseResult.length > 0) && (
            <div className="d-flex align-items-center justify-content-between gap-2 mb-4">
              <div className="sorttext">
                {false &&
                  `${lpResult.length + courseResult.length} of ${total} ${total > 1 ? "assets" : "asset"}`}
              </div>
              <div className="sort-wrapper">
                <label className="sort-label">{t("Sort by")}</label>
                <div className="custom-select-wrapper">
                  <select className="custom-select" onChange={handleSort}>
                    <option value={6}>{t("Select sort")}</option>
                    <option value={1}>Name (A-Z)</option>
                    <option value={2}>Name (Z-A)</option>
                    <option value={3}>Date (Newest)</option>
                    <option value={4}>Date (Oldest)</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      {showNotFound && (
        <NotFoundComponent
          showMsg={true}
          msgLine={`${t("no_matches_for")} ‘${query}’`}
          msgLine2={` ${t("try_another_term_or_clear_filters")}`}
        />
      )}
      {lpResult.length !== 0 && (
        <ActionPathExploreComp
          viewMoreActionPath={true}
          lpResult={lpResult}
          fromGlobal={true}
        />
      )}
      {courseResult.length !== 0 &&
        Array.from(pillarData?.entries()).map(([key, cards], pillarIndex) => {
          const [pillarId, pillarName] = key.split("|");
          const isExpanded = expandedPillarId === pillarId;
          const showViewAll = !isExpanded && cards.length > 4;
          const visibleCards = isExpanded ? cards : cards.slice(0, 4);

          return (
            <SectionHeader
              key={pillarId}
              title={pillarName}
              subtitle=""
              onViewAll={() => handleViewAll(pillarId)}
              pillarId={pillarId}
              showViewAll={true}
              from={"global"}
            >
              {cards.map((card, i) => (
                <div key={`course-${i + 1}`}>
                  <ProductCardFinwell
                    {...card}
                    componentKey={`global-search-${pillarIndex}-${i}`}
                  />
                </div>
              ))}
            </SectionHeader>
          );
        })}
    </Page>
  );
};

export default GlobalSearch;
